import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Vendor,
  VendorSummary,
  VendorDocumentType,
} from '@actschurch/ccms-sdk';

/**
 * Vendor status constants (matches VendorStatus seed data)
 */
export const VendorStatusValues = {
  /** Pending - Registration pending user account creation */
  PENDING: -5,
  /** Suspended - Temporarily suspended */
  SUSPENDED: -4,
  /** Deactivated - No longer active */
  DEACTIVATED: -3,
  /** Rejected - Application rejected */
  REJECTED: -2,
  /** More Information Required - Additional info needed */
  MORE_INFO_REQUIRED: -1,
  /** Draft - Initial registration, can edit all fields */
  DRAFT: 0,
  /** Submitted - Under review, limited editing */
  SUBMITTED: 1,
  /** UnderReview - Being reviewed by admin */
  UNDER_REVIEW: 2,
  /** Approved - Approved, pending activation */
  APPROVED: 3,
  /** Active - Fully operational */
  ACTIVE: 4,
} as const;

/**
 * Get human-readable status name
 */
export function getVendorStatusName(status: number): string {
  switch (status) {
    case VendorStatusValues.PENDING:
      return 'Pending';
    case VendorStatusValues.SUSPENDED:
      return 'Suspended';
    case VendorStatusValues.DEACTIVATED:
      return 'Deactivated';
    case VendorStatusValues.REJECTED:
      return 'Rejected';
    case VendorStatusValues.MORE_INFO_REQUIRED:
      return 'More Information Required';
    case VendorStatusValues.DRAFT:
      return 'Draft';
    case VendorStatusValues.SUBMITTED:
      return 'Submitted';
    case VendorStatusValues.UNDER_REVIEW:
      return 'Under Review';
    case VendorStatusValues.APPROVED:
      return 'Approved';
    case VendorStatusValues.ACTIVE:
      return 'Active';
    default:
      return 'Unknown';
  }
}

/**
 * Check if vendor can edit profile/documents/location
 */
export function canVendorEditProfile(status: number): boolean {
  return status === VendorStatusValues.DRAFT;
}

/**
 * Check if vendor can manage products
 */
export function canVendorManageProducts(status: number): boolean {
  return status >= VendorStatusValues.SUBMITTED;
}

/**
 * Check if vendor is fully active
 */
export function isVendorActive(status: number): boolean {
  return status === VendorStatusValues.ACTIVE;
}

/**
 * Check if vendor status is negative (blocked/suspended/rejected)
 */
export function isVendorBlocked(status: number): boolean {
  return status < 0;
}

interface VendorState {
  // Vendor data
  vendorId: number | null;
  vendorIds: number[];
  vendor: Vendor | null;
  summary: VendorSummary | null;
  documentTypes: VendorDocumentType[];

  // Loading states
  isLoading: boolean;
  isSummaryLoading: boolean;
  isDocumentTypesLoading: boolean;

  // Error states
  error: string | null;
  summaryError: string | null;

  // Flags
  hasVendor: boolean;
  isInitialized: boolean;
  /** Flag to force re-fetch of vendor context (e.g., after registration) */
  needsRefetch: boolean;
}

interface VendorActions {
  // Vendor ID management
  setVendorId: (id: number | null) => void;
  setVendorIds: (ids: number[]) => void;

  // Vendor data management
  setVendor: (vendor: Vendor | null) => void;
  setSummary: (summary: VendorSummary | null) => void;
  setDocumentTypes: (types: VendorDocumentType[]) => void;

  // Loading state management
  setLoading: (loading: boolean) => void;
  setSummaryLoading: (loading: boolean) => void;
  setDocumentTypesLoading: (loading: boolean) => void;

  // Error management
  setError: (error: string | null) => void;
  setSummaryError: (error: string | null) => void;

  // Initialization
  setInitialized: (initialized: boolean) => void;

  // Force refetch (e.g., after vendor registration)
  setNeedsRefetch: (needsRefetch: boolean) => void;
  /** Reset context and trigger refetch on next render */
  triggerRefetch: () => void;

  // Reset
  clearVendorContext: () => void;

  // Computed helpers
  canEditProfile: () => boolean;
  canManageProducts: () => boolean;
  isActive: () => boolean;
  isBlocked: () => boolean;
  getStatusName: () => string;
}

type VendorStore = VendorState & VendorActions;

const initialState: VendorState = {
  vendorId: null,
  vendorIds: [],
  vendor: null,
  summary: null,
  documentTypes: [],
  isLoading: false,
  isSummaryLoading: false,
  isDocumentTypesLoading: false,
  error: null,
  summaryError: null,
  hasVendor: false,
  isInitialized: false,
  needsRefetch: false,
};

export const useVendorStore = create<VendorStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Vendor ID management
      setVendorId: (id) =>
        set({
          vendorId: id,
          hasVendor: id !== null,
        }),

      setVendorIds: (ids) => set({ vendorIds: ids }),

      // Vendor data management
      setVendor: (vendor) =>
        set({
          vendor,
          vendorId: vendor?.vendorID ?? null,
          hasVendor: vendor !== null,
        }),

      setSummary: (summary) =>
        set({
          summary,
          vendor: summary?.vendor ?? null,
          vendorId: summary?.vendor?.vendorID ?? null,
          hasVendor: summary?.vendor !== undefined && summary?.vendor !== null,
        }),

      setDocumentTypes: (types) => set({ documentTypes: types }),

      // Loading state management
      setLoading: (loading) => set({ isLoading: loading }),
      setSummaryLoading: (loading) => set({ isSummaryLoading: loading }),
      setDocumentTypesLoading: (loading) => set({ isDocumentTypesLoading: loading }),

      // Error management
      setError: (error) => set({ error }),
      setSummaryError: (error) => set({ summaryError: error }),

      // Initialization
      setInitialized: (initialized) => set({ isInitialized: initialized }),

      // Force refetch
      setNeedsRefetch: (needsRefetch) => set({ needsRefetch }),
      triggerRefetch: () =>
        set({
          ...initialState,
          needsRefetch: true,
        }),

      // Reset
      clearVendorContext: () => set(initialState),

      // Computed helpers
      canEditProfile: () => {
        const { vendor } = get();
        if (!vendor) return false;
        return canVendorEditProfile(vendor.vendorStatusID ?? 0);
      },

      canManageProducts: () => {
        const { vendor } = get();
        if (!vendor) return false;
        return canVendorManageProducts(vendor.vendorStatusID ?? 0);
      },

      isActive: () => {
        const { vendor } = get();
        if (!vendor) return false;
        return isVendorActive(vendor.vendorStatusID ?? 0);
      },

      isBlocked: () => {
        const { vendor } = get();
        if (!vendor) return false;
        return isVendorBlocked(vendor.vendorStatusID ?? 0);
      },

      getStatusName: () => {
        const { vendor } = get();
        if (!vendor) return 'Unknown';
        return getVendorStatusName(vendor.vendorStatusID ?? 0);
      },
    }),
    {
      name: 'vendor-storage',
      // Persist vendorId and vendorIds - fetch fresh data on load
      partialize: (state) => ({
        vendorId: state.vendorId,
        vendorIds: state.vendorIds,
      }),
      // Dual-write to localStorage + sessionStorage for resilience.
      // If user clears localStorage, vendorId survives in sessionStorage.
      // If both are cleared, auth is also gone and re-login triggers fresh fetch.
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          return localStorage.getItem(name) ?? sessionStorage.getItem(name);
        },
        setItem: (name: string, value: string) => {
          try { localStorage.setItem(name, value); } catch { /* quota or access error */ }
          try { sessionStorage.setItem(name, value); } catch { /* quota or access error */ }
        },
        removeItem: (name: string) => {
          try { localStorage.removeItem(name); } catch { /* ignore */ }
          try { sessionStorage.removeItem(name); } catch { /* ignore */ }
        },
      })),
    }
  )
);

// Selectors
export const selectVendorId = (state: VendorStore) => state.vendorId;
export const selectVendor = (state: VendorStore) => state.vendor;
export const selectVendorSummary = (state: VendorStore) => state.summary;
export const selectDocumentTypes = (state: VendorStore) => state.documentTypes;
export const selectHasVendor = (state: VendorStore) => state.hasVendor;
export const selectIsVendorLoading = (state: VendorStore) => state.isLoading;
export const selectVendorError = (state: VendorStore) => state.error;
export const selectIsInitialized = (state: VendorStore) => state.isInitialized;

// Document compliance selectors
export const selectCanSubmitForReview = (state: VendorStore) =>
  state.summary?.canSubmitForReview ?? false;
export const selectMissingDocuments = (state: VendorStore) =>
  state.summary?.missingDocuments ?? [];
export const selectDocumentComplianceStatus = (state: VendorStore) =>
  state.summary?.documentComplianceStatus ?? null;
export const selectExpiringDocuments = (state: VendorStore) =>
  state.summary?.documentComplianceStatus?.expiringDocuments ?? [];

// Product metrics selectors
export const selectProductCount = (state: VendorStore) =>
  state.summary?.productCount ?? 0;
export const selectActiveProductCount = (state: VendorStore) =>
  state.summary?.activeProductCount ?? 0;
export const selectDraftProductCount = (state: VendorStore) =>
  state.summary?.draftProductCount ?? 0;

// Rating selectors
export const selectRatingCount = (state: VendorStore) =>
  state.summary?.ratingCount ?? 0;
export const selectAverageRating = (state: VendorStore) =>
  state.summary?.averageRating ?? 0;

// Status selectors
export const selectVendorStatus = (state: VendorStore) =>
  state.vendor?.vendorStatusID ?? null;
export const selectCanEditProfile = (state: VendorStore) =>
  state.vendor ? canVendorEditProfile(state.vendor.vendorStatusID ?? 0) : false;
export const selectCanManageProducts = (state: VendorStore) =>
  state.vendor ? canVendorManageProducts(state.vendor.vendorStatusID ?? 0) : false;
